import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Header2 from "../header/Header2";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StateApp } from "../context/Context";
import Loading from "../Loading/Loading";
import { notification } from "antd";
import { url } from "../url";
import nmp_mapboxgl from "@neshan-maps-platform/mapbox-gl";
import "@neshan-maps-platform/mapbox-gl/dist/NeshanMapboxGl.css";
import { getCurrentPositionWithPermission } from "../pwa/permissions";

const DEFAULT_CENTER = {
    latitude: 35.6892,
    longitude: 51.3890,
};

const asCoordinate = (value, fallback, min, max) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return fallback;
    }
    const number = Number(value);
    return Number.isFinite(number) && number >= min && number <= max ? number : fallback;
};

const Address = () => {
    const { travelID } = useParams();
    const { userInfo } = useSelector((state) => state.userinformation);
    const accessToken = userInfo.accessToken || localStorage.getItem("accessToken");
    const navigate = useNavigate();
    const { loading, setLoading, setTripPrice } = useContext(StateApp);

    const initialCenter = useMemo(() => ({
        latitude: asCoordinate(localStorage.getItem("lat"), DEFAULT_CENTER.latitude, -90, 90),
        longitude: asCoordinate(localStorage.getItem("long"), DEFAULT_CENTER.longitude, -180, 180),
    }), []);

    const [travelAddress, setTravelAddress] = useState("");
    const [latitude, setLatitude] = useState(initialCenter.latitude);
    const [longitude, setLongitude] = useState(initialCenter.longitude);
    const [locating, setLocating] = useState(false);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        if (mapRef.current) return undefined;

        let map;
        const handleMapResize = () => {
            if (map && typeof map.resize === "function") map.resize();
        };
        const handleMapClick = (event) => {
            const nextLongitude = Number(event?.lngLat?.lng);
            const nextLatitude = Number(event?.lngLat?.lat);
            if (!Number.isFinite(nextLongitude) || !Number.isFinite(nextLatitude)) return;

            setLongitude(nextLongitude);
            setLatitude(nextLatitude);
            markerRef.current?.setLngLat([nextLongitude, nextLatitude]);
        };

        try {
            map = new nmp_mapboxgl.Map({
                mapType: nmp_mapboxgl.Map.mapTypes.neshanVector,
                container: "travel-address-map",
                zoom: 14,
                pitch: 0,
                center: [initialCenter.longitude, initialCenter.latitude],
                minZoom: 2,
                maxZoom: 21,
                trackResize: true,
                mapKey: process.env.REACT_APP_NESHAN_MAP_KEY,
                poi: true,
                traffic: false,
                isZoomControlsEnabled: true,
                setZoomGesturesEnabled: true,
                mapTypeControllerOptions: {
                    show: false,
                },
            });

            const marker = new nmp_mapboxgl.Marker()
                .setLngLat([initialCenter.longitude, initialCenter.latitude])
                .addTo(map);
            mapRef.current = map;
            markerRef.current = marker;

            map.on("click", handleMapClick);
            map.on("load", handleMapResize);
            window.addEventListener("resize", handleMapResize);

            map.addControl(new nmp_mapboxgl.NavigationControl(), "top-right");
        } catch (error) {
            console.error("Neshan map initialization failed:", error);
            notification.error({
                message: "نقشه بارگذاری نشد",
                description: "می‌توانید دوباره صفحه را باز کنید.",
                placement: "topRight",
            });
        }

        return () => {
            mountedRef.current = false;
            window.removeEventListener("resize", handleMapResize);
            if (map && typeof map.off === "function") {
                map.off("click", handleMapClick);
                map.off("load", handleMapResize);
            }
            mapRef.current = null;
            markerRef.current = null;
            if (map && typeof map.remove === "function") {
                map.remove();
            }
        };
    }, [initialCenter]);

    const locateMe = async () => {
        if (locating) return;
        setLocating(true);
        try {
            const position = await getCurrentPositionWithPermission();
            if (!mountedRef.current) return;
            const { latitude: lat, longitude: lng } = position;
            setLatitude(lat);
            setLongitude(lng);
            markerRef.current?.setLngLat([lng, lat]);
            mapRef.current?.flyTo({ center: [lng, lat], zoom: 16 });
        } catch (error) {
            if (mountedRef.current) notification.warning({
                message: "موقعیت فعلی دریافت نشد",
                description: error.message || "دسترسی مکان را بررسی کنید یا نقطه را دستی روی نقشه انتخاب کنید.",
                placement: "topRight",
            });
        } finally {
            if (mountedRef.current) setLocating(false);
        }
    };

    const handleSubmit = async () => {
        const cleanAddress = travelAddress.trim();

        if (!cleanAddress) {
            notification.error({
                message: "آدرس را وارد کنید",
                placement: "topRight",
            });
            return;
        }

        if (!travelID || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            notification.error({
                message: "موقعیت انتخاب‌شده معتبر نیست",
                placement: "topRight",
            });
            return;
        }

        if (!accessToken) {
            notification.error({
                message: "ابتدا وارد حساب کاربری شوید",
                placement: "topRight",
            });
            return;
        }

        setLoading(true);

        try {
            const response = await axios.put(
                `${url}/travel/v1/set-address-and-location-for-travel`,
                {
                    travel_id: travelID,
                    address: cleanAddress,
                    location: {
                        // Keep the real coordinate sign. Do not force + / - prefixes.
                        latitude: Number(latitude),
                        longitude: Number(longitude),
                    },
                },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            const data = response?.data?.data || {};
            const {
                price,
                passenger,
                travel_date,
                origin_city,
                destination_city,
                travel_hour_time,
            } = data;

            localStorage.setItem("travelData", JSON.stringify({
                price,
                passenger,
                travel_date,
                origin_city,
                destination_city,
                travel_hour_time,
            }));
            localStorage.setItem("travelID", travelID);

            setTripPrice(price);
            window.scrollTo(0, 0);
            navigate("/Payment");
        } catch (error) {
            notification.error({
                message: error?.response?.data?.message || "خطا در ثبت آدرس",
                description: error?.message === "Network Error" ? "اتصال اینترنت را بررسی کنید." : "",
                placement: "topRight",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mk-page mk-address min-h-screen bg-[#f7f8fc]">
            <div className="md:hidden">
                <Header2 />
            </div>

            <main className="mx-auto w-[92%] max-w-[980px] py-8 md:py-4">
                <div className="mb-5">
                    <h1 className="text-[24px] font-semibold text-[#17182b]">ثبت آدرس مبدا</h1>
                    <p className="mt-2 text-[14px] leading-7 text-slate-500">
                        محل دقیق سوار شدن را روی نقشه مشخص کنید و نشانی را بنویسید.
                    </p>
                </div>

                <section className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_8px_30px_rgba(24,31,65,0.06)]">
                    <div className="p-5 md:p-3">
                        <div
                            id="travel-address-map"
                            className="h-[360px] w-full overflow-hidden rounded-[14px] bg-slate-100 md:h-[300px]"
                        />
                        <button
                            type="button"
                            className="mt-3 min-h-[46px] rounded-[10px] border border-[#082F78] bg-white px-4 text-[#082F78] disabled:opacity-60"
                            onClick={locateMe}
                            disabled={locating}
                        >
                            {locating ? "در حال دریافت موقعیت..." : "استفاده از موقعیت فعلی من"}
                        </button>
                        <p className="mt-2 text-[12px] leading-6 text-slate-500">
                            دسترسی مکان فقط با انتخاب این دکمه درخواست می‌شود؛ انتخاب دستی نقشه همچنان فعال است.
                        </p>

                        <div className="mt-6">
                            <label htmlFor="travel-address" className="mb-2 block text-[15px] font-medium text-slate-700">
                                نشانی
                            </label>
                            <textarea
                                id="travel-address"
                                value={travelAddress}
                                onChange={(event) => setTravelAddress(event.target.value)}
                                rows={3}
                                className="w-full resize-none rounded-[12px] border border-slate-300 px-4 py-3 leading-7"
                                placeholder="مثلاً خیابان آزادی، بعد از میدان، پلاک ۱۲"
                            />
                        </div>

                        <div className="mt-6 flex justify-end md:justify-center">
                            <button
                                type="button"
                                disabled={Boolean(loading)}
                                onClick={handleSubmit}
                                className="flex h-[50px] min-w-[210px] items-center justify-center rounded-[11px] bg-[#4F48EC] px-7 text-white md:w-full"
                            >
                                {loading ? <Loading /> : "ثبت و ادامه"}
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Address;
